import { useApiQuery } from '@/api/main/query'
import {
  fetchMerchantFailure,
  fetchMerchantStart,
  fetchMerchantSuccess,
  MerchantModel,
  MerchantShortModel,
} from '@/entities/merchant'
import { Roles } from '@/entities/roles'
import { USER_ROLE_KEY } from '@/features/auth'
import { useToast } from '@/shared/hooks'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { useEffect, useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

export const useMerchantData = () => {
  const dispatch = useDispatch()
  const { addToast } = useToast()
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const shouldFetch = userRole === Roles.Merchant

  // Мемоизированная версия addToast
  const addToastMemoized = useCallback(
    (title: string, message: string, type: 'error' | 'success' | 'default') => {
      addToast(title, message, type)
    },
    [addToast]
  )

  const {
    data: merchantBase,
    error: merchantBaseError,
    isLoading: isLoadingBase,
  } = useApiQuery<MerchantModel>(['merchantBase', userRole], '/v1/user/merchant', undefined, {
    enabled: shouldFetch,
  })

  const {
    data: merchantUrls,
    error: merchantUrlsError,
    isLoading: isLoadingUrls,
  } = useApiQuery<MerchantShortModel>(['merchantUrls', userRole], '/v1/merchant', undefined, {
    enabled: shouldFetch,
  })

  // Мемоизированные данные мерчанта
  const mergedMerchantData = useMemo(() => {
    if (!merchantBase || !merchantUrls) return null
    return {
      ...merchantBase,
      ...merchantUrls,
    } as MerchantModel
  }, [merchantBase, merchantUrls])

  // Мемоизированная ошибка
  const merchantError = useMemo(() => {
    return merchantBaseError || merchantUrlsError
  }, [merchantBaseError, merchantUrlsError])

  // Эффект для старта загрузки
  useEffect(() => {
    if (!shouldFetch) return
    dispatch(fetchMerchantStart())
  }, [dispatch, shouldFetch])

  // Эффект для обработки ошибок
  useEffect(() => {
    if (!shouldFetch || !merchantError) return

    const key = 'merchant-error-handled'
    const alreadyShown = sessionStorage.getItem(key)
    if (alreadyShown === merchantError.message) return

    sessionStorage.setItem(key, merchantError.message)
    dispatch(fetchMerchantFailure(merchantError.message))
    addToastMemoized('Ошибка!', merchantError.message, 'error')
  }, [merchantError, dispatch, shouldFetch, addToastMemoized])

  // Эффект для успешной загрузки
  useEffect(() => {
    if (!shouldFetch || !mergedMerchantData) return

    sessionStorage.removeItem('merchant-error-handled')
    dispatch(fetchMerchantSuccess(mergedMerchantData))
  }, [mergedMerchantData, dispatch, shouldFetch])

  return {
    dataMerchant: mergedMerchantData,
    errorMerchant: merchantError,
    isMerchant: shouldFetch,
    isLoading: shouldFetch && (isLoadingBase || isLoadingUrls),
  }
}
